import { api, getAccessToken, type StockLot, type StockLotDetail, type StoresOverview } from '../api';
import { callout, close, field, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { bindDocumentViewers } from './document';
import { openPdfViewer } from './pdf-viewer';
import { KIND_LABEL, LOCATION_KIND_LABEL, MOVEMENT_LABEL, TAG_STYLE, expiryText } from '../stock-ui';
import { esc, fmtDate, html, pill, toast } from '../ui';

/**
 * Everything that happens to one lot.
 *
 * Each of these is a distinct act with its own record, not a field on an edit
 * form — because that is what they are in the store. A part is inspected, or
 * condemned, or mutilated, or sent back; each leaves a different trail, and an
 * "edit lot" dialog that let somebody flip a condition from a dropdown would
 * erase the difference between all of them.
 */

const peopleOptions = (o: StoresOverview, selected?: string) =>
  [
    opt('', 'Choose…'),
    ...o.people.map((p) => opt(p.id, p.jobTitle ? `${p.displayName} — ${p.jobTitle}` : p.displayName, p.id === selected)),
  ].join('');

/** What the sale price will be if it is left blank. */
const markupHint = (o: StoresOverview) =>
  o.stockMarkupPercent === null
    ? 'Left blank, no sale price is set — the part will not be billed'
    : `Left blank, ${o.stockMarkupPercent}% is added to the cost. Set on Organisation info`;

const money = (n: number | null, currency: string | null) =>
  n === null ? null : `${n.toFixed(2)} ${currency ?? ''}`.trim();

/** What each filed paper is, in the words the store uses for it. */
const PAPER_ROLE: Record<string, string> = {
  INVOICE: 'Invoice',
  CONFORMITY: 'Certificate of conformity',
  RELEASE: 'Airworthiness release',
  ANALYSIS: 'Certificate of analysis',
  OTHER: 'Filed with the delivery',
};

const locationOptions = (o: StoresOverview, kind: string) => {
  const list = o.locations.filter((l) => l.kind === kind);
  return list.length
    ? list.map((l) => opt(l.id, `${l.code} — ${l.name}`)).join('')
    : opt('', 'no location of that kind yet');
};

// ── receiving ───────────────────────────────────────────────────────────────

/**
 * The paperwork a lot came with, on a form.
 *
 * One piece, used by Receive stock and by Correct the details, so the two
 * cannot drift: the same file box, the same page picker, the same rule about
 * what happens on cancel. A form holds one of these and asks it for its
 * markup, wires it after each render, and collects from it on submit.
 *
 * The file is uploaded the moment it is picked, so the page count comes back
 * while the form is still open and the pages to keep can be ticked against a
 * preview. The preview is the file itself, straight from the picker — no round
 * trip — shown in the browser's own viewer, which numbers its pages.
 */
interface ChosenPaper {
  attachmentId: string;
  fileName: string;
  pageCount: number;
  previewUrl: string | null;
  /** 1-based. Starts as every page. */
  keep: Set<number>;
}

function paperPicker(opts: {
  /** What the lot already has on file, if anything — shown, with a way to replace it. */
  existing?: { id: string; fileName: string } | null;
  /** The one line under the file box. */
  hint: string;
}) {
  let paper: ChosenPaper | null = null;
  let uploading: string | null = null;
  /** Set once somebody asks to replace what is on file; the box appears. */
  let replacing = false;

  const dropPreview = () => {
    if (paper?.previewUrl) URL.revokeObjectURL(paper.previewUrl);
  };

  const box = () => html`
    <label class="btn filepick">
      <input type="file" accept="application/pdf,image/*" data-paper>
      Choose a file
    </label>
    <span class="hint">${opts.hint}</span>
  `;

  return {
    body(): string {
      if (paper) {
        return html`
          <div class="paper">
            <div>
              <div><b>${esc(paper.fileName)}</b>
                <span class="tiny dim"> · ${paper.pageCount === 0 ? 'could not be read for pages' : `${paper.pageCount} page${paper.pageCount === 1 ? '' : 's'}`}</span>
                <button type="button" class="btn sm" data-paper-remove style="margin-left:8px">Choose another</button>
              </div>
              ${paper.pageCount > 1
                ? html`
                    <span class="hint">Tick the pages that are this lot's paperwork. The rest are cut off before it is
                      filed — a supplier's scan is usually the whole delivery, and the two pages that are this
                      lot's certificate are what an auditor asks for.</span>
                    <div class="pages">
                      ${Array.from({ length: paper.pageCount }, (_, i) => i + 1).map((n) => html`
                        <label><input type="checkbox" data-page="${n}" ${paper!.keep.has(n) ? 'checked' : ''}> ${n}</label>
                      `).join('')}
                    </div>
                    <div style="margin-top:6px">
                      <button type="button" class="btn sm" data-pages-all>All</button>
                      <button type="button" class="btn sm" data-pages-none>None</button>
                    </div>
                  `
                : ''}
            </div>
            ${paper.previewUrl
              ? html`<iframe class="paperprev" src="${esc(paper.previewUrl)}" title="${esc(paper.fileName)}"></iframe>`
              : ''}
          </div>
        `;
      }
      if (uploading) return html`<span class="tiny dim">Uploading ${esc(uploading)}…</span>`;
      if (opts.existing && !replacing) {
        return html`
          <div>
            <button type="button" class="docopen" data-view="${esc(opts.existing.id)}" data-title="${esc(opts.existing.fileName)}"
                    data-sub="The paperwork filed with this lot"><b>${esc(opts.existing.fileName)}</b></button>
            <span class="tiny dim"> · on file</span>
            <button type="button" class="btn sm" data-paper-replace style="margin-left:8px">Replace</button>
          </div>
          <span class="hint">Opens in the viewer. Replacing it files a new document against the lot; the one on file now stays
            in storage and is named on the audit trail.</span>
        `;
      }
      return box();
    },

    bind(root: HTMLElement, refresh: () => void): void {
      root.querySelector<HTMLInputElement>('[data-paper]')?.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        uploading = file.name;
        refresh();
        try {
          const up = await api.stores.paperwork.upload(file);
          paper = {
            attachmentId: up.attachmentId,
            fileName: up.fileName,
            pageCount: up.pageCount,
            previewUrl: URL.createObjectURL(file),
            keep: new Set(Array.from({ length: up.pageCount }, (_, i) => i + 1)),
          };
        } catch (err) {
          toast(err instanceof Error ? err.message : 'The file could not be uploaded.', 'error');
        } finally {
          uploading = null;
          refresh();
        }
      });
      root.querySelector('[data-paper-replace]')?.addEventListener('click', () => {
        replacing = true;
        refresh();
      });
      root.querySelector('[data-paper-remove]')?.addEventListener('click', () => {
        if (!paper) return;
        const gone = paper;
        dropPreview();
        paper = null;
        refresh();
        api.stores.paperwork.discard(gone.attachmentId).catch(() => undefined);
      });
      root.querySelectorAll<HTMLInputElement>('[data-page]').forEach((cb) =>
        cb.addEventListener('change', () => {
          const n = Number(cb.dataset.page);
          if (cb.checked) paper?.keep.add(n); else paper?.keep.delete(n);
        }),
      );
      root.querySelector('[data-pages-all]')?.addEventListener('click', () => {
        if (!paper) return;
        paper.keep = new Set(Array.from({ length: paper.pageCount }, (_, i) => i + 1));
        refresh();
      });
      root.querySelector('[data-pages-none]')?.addEventListener('click', () => {
        if (!paper) return;
        paper.keep.clear();
        refresh();
      });
      bindDocumentViewers(root);
    },

    /** What to send, or why not yet. Nothing when no new file was chosen. */
    collect(): { attachmentId?: string; keepPages?: number[] } | { error: string } {
      if (uploading) return { error: `${uploading} is still uploading — a moment.` };
      if (!paper) return {};
      if (paper.pageCount > 1 && paper.keep.size === 0) {
        return { error: 'Keep at least one page of the paperwork, or choose another file.' };
      }
      // Only a strict subset is sent: the whole file kept is the file as uploaded.
      const keepPages = paper.pageCount > 1 && paper.keep.size < paper.pageCount
        ? [...paper.keep].sort((a, b) => a - b)
        : undefined;
      return { attachmentId: paper.attachmentId, keepPages };
    },

    /** Uploaded and then not used: the form was cancelled, so the file would otherwise sit in storage pointing at nothing. */
    onClose(submitted: boolean): void {
      dropPreview();
      if (!submitted && paper) api.stores.paperwork.discard(paper.attachmentId).catch(() => undefined);
    },
  };
}

/**
 * A part arrives.
 *
 * The form asks for the paperwork in the same breath as the part, because the
 * two arrive together and separating them is how a box ends up on a shelf with
 * its certificate still in the envelope. What counts as acceptable paperwork
 * follows the class of item — 145.A.42(a) — so the choice narrows as soon as
 * the class is picked rather than being refused on submit. The document itself
 * is taken as a file as well, so it is filed with the lot rather than described
 * and left in the envelope.
 */
export function receiveLot(o: StoresOverview, reload: () => void): void {
  let kind = 'CONSUMABLE';
  const paperwork = paperPicker({
    hint: 'The Form 1, the certificate of conformity or of analysis, the delivery note — as a PDF or a photo. ' +
      'Filed with the lot, beside what is typed about it above.',
  });

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Receive stock',
    sub: 'It lands in quarantine — nothing is fittable until it has been inspected',
    primary: 'Receive it',
    body: () => {
      const acceptable = o.reference.acceptableRelease[kind] ?? [];
      const releaseChoices = o.reference.releaseKinds.filter((r) => acceptable.includes(r.kind));

      return html`
        <div class="frow3">
          ${field('What it is', select('kind', o.reference.kinds
            .map((k) => opt(k, KIND_LABEL[k] ?? k, k === kind)).join(''), 'data-kind'))}
          ${field('Part number', input('partNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('ATA chapter', input('ataChapter', '', 'placeholder="79" maxlength="8"'))}
        </div>
        ${field('Description', input('description', '', 'placeholder="AeroShell Oil Diesel Ultra" maxlength="200"'))}

        <div class="frow3">
          ${field('Batch / lot', input('batchLot', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Serial number', input('serialNumber', '', 'style="font-family:var(--mono)" maxlength="60"'), 'A serialised item is received as one')}
          ${field('Quantity', input('quantity', '1', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        <div class="frow3">
          ${field('Unit', select('unit', o.reference.units.map((u) => opt(u, u, u === 'EA')).join('')))}
          ${field('Manufacturer', input('manufacturer', '', 'maxlength="160"'))}
          ${field('Supplier', input('supplier', '', 'maxlength="160"'))}
        </div>

        <div class="frow3">
          ${field('Order reference', input('orderReference', '', 'maxlength="60"'))}
          ${field('Manufactured / cured', input('manufacturedAt', '', 'type="date"'), 'The clock an elastomer runs on')}
          ${field('Received', input('receivedAt', todayISO(), 'type="date"'))}
        </div>

        <div class="frow3">
          ${field('Shelf life expires', input('shelfLifeExpiry', '', 'type="date"'), 'Leave empty where there is none')}
          ${field('Taken from', select('shelfLifeSource', o.reference.shelfLifeSources
            .map((s) => opt(s, s === 'CoA' ? 'The certificate of analysis' : s === 'MANUFACTURER' ? 'The manufacturer' : 'A house rule')).join('')))}
          ${field('Currency', input('currency', o.currency ?? '', 'maxlength="3" style="text-transform:uppercase"'))}
        </div>

        <div class="frow3">
          ${field('Unit cost', input('unitCost', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)" data-cost'), 'What it cost to buy')}
          ${field('Sale price', input('salePrice', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)" data-sale'), markupHint(o))}
          <div></div>
        </div>

        <div class="frow3">
          ${field('Release document', select('releaseKind', [
            opt('', 'None yet'),
            ...releaseChoices.map((r) => opt(r.kind, r.label)),
          ].join('')))}
          ${field('Its reference', input('releaseReference', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Issued by', input('releaseIssuer', '', 'maxlength="160"'))}
        </div>

        <div class="fset">
          <div class="lg">The paperwork it came with</div>
          ${paperwork.body()}
        </div>

        ${callout('info', 'i',
          kind === 'COMPONENT'
            ? html`A component is released on an <b>EASA Form 1</b> or an <b>FAA 8130-3</b>, and nothing
                else will do — 145.A.42(a)(i).`
            : html`Material needs a <b>conformity to specification statement</b> naming both the
                manufacturing and the supplier source — 145.A.42(a)(v). A certificate of conformity or of
                analysis is that statement; a Form 1 is not expected, and demanding one would make the
                store unusable.`)}
      `;
    },
    onClose: (submitted) => paperwork.onClose(submitted),
    onRender: (root, refresh) => {
      paperwork.bind(root, refresh);
      const cost = root.querySelector<HTMLInputElement>('[data-cost]');
      const sale = root.querySelector<HTMLInputElement>('[data-sale]');
      if (cost && sale && o.stockMarkupPercent !== null) {
        cost.addEventListener('input', () => {
          // Only while the sale price is untouched. Once somebody types
          // their own number, the markup stops overwriting it.
          if (sale.dataset.touched) return;
          const n = Number(cost.value);
          sale.value = Number.isFinite(n) && n > 0
            ? (Math.round(n * (1 + o.stockMarkupPercent! / 100) * 100) / 100).toFixed(2)
            : '';
        });
        sale.addEventListener('input', () => { sale.dataset.touched = '1'; });
      }
      root.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
        kind = (e.target as HTMLSelectElement).value;
        // Refreshed so the acceptable paperwork changes with the class rather
        // than being refused after the form is filled in.
        refresh();
      });
    },
    collect: (root) => {
      const partNumber = val(root, 'partNumber');
      const description = val(root, 'description');
      const quantity = numVal(root, 'quantity');
      if (!partNumber) return { error: 'A part number is what everything else hangs off.' };
      if (!description) return { error: 'Describe it — a part number alone is unreadable on a shelf.' };
      if (!quantity || quantity <= 0) return { error: 'How much arrived?' };
      const paper = paperwork.collect();
      if ('error' in paper) return paper;

      return {
        kind: val(root, 'kind'),
        partNumber,
        description,
        ataChapter: val(root, 'ataChapter') || undefined,
        batchLot: val(root, 'batchLot') || undefined,
        serialNumber: val(root, 'serialNumber') || undefined,
        quantity,
        unit: val(root, 'unit'),
        manufacturer: val(root, 'manufacturer') || undefined,
        supplier: val(root, 'supplier') || undefined,
        orderReference: val(root, 'orderReference') || undefined,
        manufacturedAt: val(root, 'manufacturedAt') || undefined,
        receivedAt: val(root, 'receivedAt') || undefined,
        shelfLifeExpiry: val(root, 'shelfLifeExpiry') || undefined,
        shelfLifeSource: val(root, 'shelfLifeExpiry') ? val(root, 'shelfLifeSource') : undefined,
        unitCost: numVal(root, 'unitCost'),
        salePrice: numVal(root, 'salePrice'),
        currency: val(root, 'currency') || undefined,
        releaseKind: val(root, 'releaseKind') || undefined,
        releaseReference: val(root, 'releaseReference') || undefined,
        releaseIssuer: val(root, 'releaseIssuer') || undefined,
        ...paper,
      };
    },
    submit: (body) => api.stores.receive(body),
    done: (r) => `${r.partNumber} received into quarantine`,
    onDone: (r: StockLotDetail) => {
      if (r.paperworkGap) {
        toast(`${r.partNumber}: ${r.paperworkGap}`, 'error');
      }
      reload();
    },
  });
}

/**
 * The incoming inspection — the gate the whole store hangs on.
 *
 * A failure asks where it goes, because both answers are real: back to the
 * supplier, or into the unserviceable store. They leave different trails and
 * neither is a deletion.
 */
export function inspectLot(lot: StockLot, o: StoresOverview, reload: () => void): void {
  let outcome: 'PASS' | 'FAIL' = 'PASS';

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Incoming inspection',
    sub: `${lot.partNumber} — ${lot.description.slice(0, 60)}`,
    primary: 'Record the inspection',
    body: () => html`
      ${lot.paperworkGap
        ? callout('caution', '!', html`
            <b>${esc(lot.paperworkGap)}</b><br>
            An inspection cannot pass without it. Record the document against the lot first — <b>Edit</b> on
            its row — or fail the inspection and send it back.
          `)
        : ''}

      ${lot.attachmentId
        ? html`<p class="tiny" style="margin:0 0 10px">
            <button type="button" class="docopen" data-view="${esc(lot.attachmentId)}" data-title="${esc(lot.partNumber)}"
                    data-sub="The paperwork filed with this lot">Open the paperwork it came with</button>
          </p>`
        : ''}

      <div class="frow3">
        ${field('Outcome', select('outcome', [
          opt('PASS', 'Passes — it is what it should be', outcome === 'PASS'),
          opt('FAIL', 'Fails — it is not', outcome === 'FAIL'),
        ].join(''), 'data-outcome'))}
        ${field('Inspected by', select('inspectedByPersonId', peopleOptions(o)))}
        ${field('On', input('inspectedAt', todayISO(), 'type="date"'))}
      </div>

      ${outcome === 'PASS'
        ? html`
            ${field('Onto which shelf', select('locationId', locationOptions(o, 'SERVICEABLE')))}
            ${field('Note', input('note', '', 'placeholder="Optional" maxlength="1000"'))}
            ${callout('info', 'i', html`
              Check that it is the part ordered, that the documents are with it and match, and that it is
              undamaged and within any life it arrived with. Passing it is what makes it fittable.
            `)}
          `
        : html`
            <div class="frow">
              ${field('What happens to it', select('disposition', [
                opt('UNSERVICEABLE', 'Into the unserviceable store'),
                opt('RETURN_TO_SUPPLIER', 'Back to the supplier'),
              ].join('')))}
              ${field('Return reference', input('returnReference', '', 'placeholder="RMA number" maxlength="60"'))}
            </div>
            ${field('What was wrong with it', input('note', '', 'placeholder="Seal perished; cure date four years before delivery" maxlength="1000"'))}
          `}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-outcome]')?.addEventListener('change', (e) => {
        outcome = (e.target as HTMLSelectElement).value as 'PASS' | 'FAIL';
        refresh();
      });
      bindDocumentViewers(root);
    },
    collect: (root) => {
      const inspectedByPersonId = val(root, 'inspectedByPersonId');
      if (!inspectedByPersonId) return { error: 'Who inspected it?' };
      const result = val(root, 'outcome');
      const note = val(root, 'note');
      if (result === 'FAIL' && !note) {
        return { error: 'Say what was wrong with it — a rejection with no reason cannot be acted on.' };
      }
      return {
        outcome: result,
        inspectedByPersonId,
        inspectedAt: val(root, 'inspectedAt'),
        note: note || undefined,
        locationId: val(root, 'locationId') || undefined,
        disposition: result === 'FAIL' ? val(root, 'disposition') : undefined,
        returnReference: val(root, 'returnReference') || undefined,
      };
    },
    submit: (body) => api.stores.inspect(lot.id, body),
    done: (r) => `${r.partNumber} — ${r.stateLabel.toLowerCase()}`,
    onDone: reload,
  });
}

// ── the yellow tag ──────────────────────────────────────────────────────────

/**
 * A part taken off an aircraft, to go back on that aircraft.
 *
 * The case the store was described from: two identical batteries in the shop,
 * one off each of two aeroplanes. The record is bound to its aircraft from the
 * moment it is created, and the store refuses to issue it to any other.
 */
export function reserveFromAircraft(o: StoresOverview, reload: () => void): void {
  let aircraftId = o.aircraft[0]?.id ?? '';

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Part off an aircraft',
    sub: 'Removed to get at something else, and going back on when the job is done',
    primary: 'Tag it',
    body: () => {
      const packs = o.workOrders.filter((w) => w.aircraftId === aircraftId);
      return html`
        <div class="frow">
          ${field('Off which aircraft', select('aircraftId', o.aircraft
            .map((a) => opt(a.id, `${a.registration} — ${a.typeModel}`, a.id === aircraftId)).join(''), 'data-aircraft'))}
          ${field('Under which work pack', select('workOrderId', [
            opt('', packs.length ? 'None — not on a pack' : 'no open pack on this aircraft'),
            ...packs.map((w) => opt(w.id, `${w.reference} — ${w.title.slice(0, 50)}`)),
          ].join('')))}
        </div>
        <div class="frow3">
          ${field('Part number', input('partNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Serial number', input('serialNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Quantity', input('quantity', '1', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        ${field('Description', input('description', '', 'placeholder="Main battery, Concorde RG-35AXC" maxlength="200"'))}
        <div class="frow">
          ${field('Removed by', select('removedByPersonId', peopleOptions(o)))}
          ${field('On', input('removedAt', todayISO(), 'type="date"'))}
        </div>
        ${field('Why it came off', input('note', '', 'placeholder="Removed for access to the firewall" maxlength="400"'))}
        ${callout('caution', '!', html`
          It goes into quarantine on a yellow tag naming the aircraft it came off, and the store will
          refuse to fit it to any other. That is the whole point of the tag: two identical parts from two
          aeroplanes on the same bench is how they get swapped.
        `)}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-aircraft]')?.addEventListener('change', (e) => {
        aircraftId = (e.target as HTMLSelectElement).value;
        // The work-pack list is per aircraft, and offering a pack against
        // another aeroplane is exactly the mix-up this dialog prevents.
        refresh();
      });
    },
    collect: (root) => {
      const partNumber = val(root, 'partNumber');
      const description = val(root, 'description');
      const removedByPersonId = val(root, 'removedByPersonId');
      if (!partNumber || !description) return { error: 'A part number and a description, so it can be found again.' };
      if (!removedByPersonId) return { error: 'Who took it off?' };
      return {
        aircraftId: val(root, 'aircraftId'),
        workOrderId: val(root, 'workOrderId') || undefined,
        partNumber,
        description,
        serialNumber: val(root, 'serialNumber') || undefined,
        quantity: numVal(root, 'quantity') ?? 1,
        removedByPersonId,
        removedAt: val(root, 'removedAt'),
        note: val(root, 'note') || undefined,
      };
    },
    submit: (body) => api.stores.reserve(body),
    done: (r) => `${r.partNumber} tagged to ${r.sourceAircraft?.registration ?? 'its aircraft'}`,
    onDone: reload,
  });
}

export function releaseReservation(lot: StockLotDetail, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Release the reservation',
    sub: `${lot.partNumber} — off ${lot.sourceAircraft?.registration ?? 'an aircraft'}`,
    primary: 'Release it',
    body: () => html`
      ${field('Why it is not going back on', input('reason', '', 'placeholder="Aircraft sold; part retained as spare" maxlength="400"'))}
      ${callout('caution', '!', html`
        It goes back into <b>quarantine</b>, not onto the serviceable shelf. A part that came off an
        aircraft has never been through an incoming inspection, and this is the point at which somebody
        has to look at it before it can be fitted to anything.
      `)}
    `,
    collect: (root) => {
      const reason = val(root, 'reason');
      if (!reason) return { error: 'Say why it is no longer going back on its own aircraft.' };
      return { reason };
    },
    submit: (body) => api.stores.releaseReservation(lot.id, body),
    done: () => 'Reservation released — the lot is back in quarantine',
    onDone: reload,
  });
}

// ── condition changes ───────────────────────────────────────────────────────

export function openLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Record it as opened',
    sub: `${lot.partNumber} — ${lot.description.slice(0, 60)}`,
    primary: 'Record it',
    body: () => html`
      <div class="frow">
        ${field('Opened on', input('openedAt', todayISO(), 'type="date"'))}
        ${field('Good for, once open', input('openedLifeDays', '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'), 'Days, from the manufacturer')}
      </div>
      ${field('Recorded by', select('personId', peopleOptions(o)))}
      ${callout('info', 'i', html`
        This is the third clock. Shelf life runs from manufacture and stops at fitment; the opened life
        runs from today, and where both apply the store uses whichever expires first — a sealant good
        until 2030 is finished a week after it is mixed.
      `)}
    `,
    collect: (root) => ({
      openedAt: val(root, 'openedAt'),
      openedLifeDays: numVal(root, 'openedLifeDays'),
      personId: val(root, 'personId') || undefined,
    }),
    submit: (body) => api.stores.open(lot.id, body),
    done: (r) => (r.effectiveExpiry ? `Opened — good until ${fmtDate(r.effectiveExpiry)}` : 'Opened'),
    onDone: reload,
  });
}

export function transferLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  // Only bins the lot's condition may legally sit in are offered: a move that
  // would break segregation is not a choice the screen should present and then
  // refuse.
  const home: Record<string, string> = {
    QUARANTINE: 'QUARANTINE', RESERVED: 'QUARANTINE', SERVICEABLE: 'SERVICEABLE',
    UNSERVICEABLE: 'UNSERVICEABLE', UNSALVAGEABLE: 'SCRAP',
  };
  const kind = home[lot.state];

  openDialog<Record<string, unknown>>({
    title: 'Move it',
    sub: `${lot.partNumber} — currently ${lot.location ? lot.location.code : 'nowhere'}`,
    primary: 'Move it',
    body: () => html`
      ${field('To', select('locationId', locationOptions(o, kind ?? 'SERVICEABLE')))}
      ${field('Note', input('note', '', 'maxlength="400"'))}
      ${kind
        ? callout('info', 'i', html`
            Only ${esc(LOCATION_KIND_LABEL[kind].toLowerCase())} locations are offered.
            ${esc(lot.stateLabel)} stock has to be kept apart from the rest — 145.A.25(d).
          `)
        : ''}
    `,
    collect: (root) => {
      const locationId = val(root, 'locationId');
      if (!locationId) return { error: 'There is no location of the right kind. Add one first.' };
      return { locationId, note: val(root, 'note') || undefined };
    },
    submit: (body) => api.stores.transfer(lot.id, body),
    done: (r) => `Moved to ${r.location?.code ?? 'its new location'}`,
    onDone: reload,
  });
}

export function condemnLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Condemn it',
    sub: `${lot.partNumber} — ${lot.description.slice(0, 60)}`,
    primary: 'Condemn it',
    body: () => html`
      ${field('Why it is unserviceable', input('reason', '', 'placeholder="Beyond shelf life; hardened" maxlength="400"'))}
      ${field('Recorded by', select('personId', peopleOptions(o)))}
      ${callout('caution', '!', html`
        It moves to the unserviceable store and can no longer be fitted. The record stays — an
        unserviceable part is controlled, not deleted, and where it went is part of the shelf's history.
      `)}
    `,
    collect: (root) => {
      const reason = val(root, 'reason');
      if (!reason) return { error: 'Say why it is unserviceable.' };
      return { reason, personId: val(root, 'personId') || undefined };
    },
    submit: (body) => api.stores.condemn(lot.id, body),
    done: (r) => `${r.partNumber} condemned`,
    onDone: reload,
  });
}

/**
 * Scrapping, under 145.A.42(d).
 *
 * The mutilation is the control, not the label. A part merely marked scrap can
 * be found in a skip and fitted to an aeroplane, so how it was destroyed and
 * who watched are asked for rather than offered.
 */
export function mutilateLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Scrap it',
    sub: `${lot.partNumber} — unsalvageable, 145.A.42(d)`,
    primary: 'Record the mutilation',
    body: () => html`
      ${field('How it was mutilated', input('method', '', 'placeholder="Housing drilled through in four places and flange cut" maxlength="300"'))}
      <div class="frow3">
        ${field('By', select('mutilatedByPersonId', peopleOptions(o)))}
        ${field('Witnessed by', select('witnessedByPersonId', peopleOptions(o)))}
        ${field('On', input('at', todayISO(), 'type="date"'))}
      </div>
      ${callout('caution', '!', html`
        145.A.42(d) asks for an unsalvageable part to be made permanently unusable. A note that says
        "scrapped" does not show that it was — what was physically done to it is the record.
      `)}
    `,
    collect: (root) => {
      const method = val(root, 'method');
      const mutilatedByPersonId = val(root, 'mutilatedByPersonId');
      if (!method) return { error: 'Record what was physically done to it.' };
      if (!mutilatedByPersonId) return { error: 'Who mutilated it?' };
      return {
        method,
        mutilatedByPersonId,
        witnessedByPersonId: val(root, 'witnessedByPersonId') || undefined,
        at: val(root, 'at'),
      };
    },
    submit: (body) => api.stores.mutilate(lot.id, body),
    done: (r) => `${r.partNumber} scrapped`,
    onDone: reload,
  });
}

export function returnToSupplier(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Send it back',
    sub: `${lot.partNumber} — to ${lot.supplier ?? 'the supplier'}`,
    primary: 'Record the return',
    body: () => html`
      <div class="frow">
        ${field('Return reference', input('reference', '', 'placeholder="RMA number" maxlength="60"'))}
        ${field('Recorded by', select('personId', peopleOptions(o)))}
      </div>
      ${field('Note', input('note', '', 'maxlength="400"'))}
    `,
    collect: (root) => ({
      reference: val(root, 'reference') || undefined,
      note: val(root, 'note') || undefined,
      personId: val(root, 'personId') || undefined,
    }),
    submit: (body) => api.stores.returnToSupplier(lot.id, body),
    done: (r) => `${r.partNumber} returned to the supplier`,
    onDone: reload,
  });
}

/**
 * What it cost, and what it sells for.
 *
 * The sale price is what reaches the invoice when the pack is released, so a
 * lot without one is fittable and unbillable — which is how three litres of
 * oil went onto an aeroplane and onto nobody's account.
 *
 * Editing here changes the shelf, not history. A part already issued carries
 * the price it was issued at, and repricing cannot reach back into it.
 */
export function repriceLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Prices',
    sub: `${lot.partNumber} — ${lot.description.slice(0, 60)}`,
    primary: 'Save the prices',
    body: () => html`
      <div class="frow3">
        ${field('Unit cost', input('unitCost', lot.unitCost === null ? '' : lot.unitCost.toFixed(2),
          'inputmode="decimal" style="text-align:right;font-family:var(--mono)" data-cost'), 'What it cost to buy')}
        ${field('Sale price', input('salePrice', lot.salePrice === null ? '' : lot.salePrice.toFixed(2),
          'inputmode="decimal" style="text-align:right;font-family:var(--mono)" data-sale'), 'What the customer is charged')}
        ${field('Currency', input('currency', lot.currency ?? o.currency ?? '', 'maxlength="3" style="text-transform:uppercase"'),
          'What the supplier invoiced in')}
      </div>
      ${(lot.currency ?? o.currency) !== o.currency
        ? html`
            <div class="frow">
              ${field(
                `1 ${lot.currency} in ${o.currency ?? 'the billing currency'}`,
                input('exchangeRate', lot.exchangeRate === null ? '' : String(lot.exchangeRate),
                  'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
                'The sale price is always in the currency you bill in',
              )}
              <div></div>
            </div>
            ${callout('caution', '!', html`
              This lot was bought in <b>${esc(lot.currency ?? '')}</b> and is billed in
              <b>${esc(o.currency ?? '')}</b>. Without a rate it has no sale price and the release
              will not charge for it — nothing here converts at a rate nobody supplied.
            `)}
          `
        : ''}
      ${o.stockMarkupPercent !== null
        ? html`<button type="button" class="btn sm" data-apply-markup>
            Apply the ${o.stockMarkupPercent}% markup
          </button>`
        : ''}
      ${lot.fitments.length
        ? callout('info', 'i', html`
            This lot has already been issued ${lot.fitments.length} time(s). Those parts keep the price
            they were issued at — changing it here affects what is still on the shelf, not what has
            already been charged.
          `)
        : ''}
    `,
    onRender: (root) => {
      root.querySelector('[data-apply-markup]')?.addEventListener('click', () => {
        const cost = Number(root.querySelector<HTMLInputElement>('[data-cost]')?.value);
        const sale = root.querySelector<HTMLInputElement>('[data-sale]');
        if (!Number.isFinite(cost) || cost <= 0 || !sale) {
          return toast('Put a cost in first — the markup is applied to it.', 'error');
        }
        sale.value = (Math.round(cost * (1 + o.stockMarkupPercent! / 100) * 100) / 100).toFixed(2);
      });
    },
    collect: (root) => {
      const unitCost = numVal(root, 'unitCost');
      const salePrice = numVal(root, 'salePrice');
      const exchangeRate = numVal(root, 'exchangeRate');
      if (unitCost === undefined && salePrice === undefined && exchangeRate === undefined) {
        return { error: 'Put in a cost, a sale price, or both.' };
      }
      if (exchangeRate !== undefined && !(exchangeRate > 0)) {
        return { error: 'A rate is a number greater than zero.' };
      }
      return {
        unitCost: unitCost ?? null,
        salePrice: salePrice ?? null,
        currency: val(root, 'currency') || undefined,
        exchangeRate,
      };
    },
    submit: (body) => api.stores.reprice(lot.id, body),
    done: (r) =>
      r.salePrice === null
        ? 'Prices saved — no sale price, so this lot will not be billed'
        : `Sale price ${r.salePrice.toFixed(2)}${r.marginPercent === null ? '' : ` · +${r.marginPercent}%`}`,
    onDone: reload,
  });
}

/**
 * Correcting what a lot says about itself.
 *
 * Not a way to change a lot's condition — that is what inspection, condemning
 * and the rest are for, and each leaves its own trail. This is for the digit
 * mistyped off an invoice: a batch number, a shelf life read from the wrong
 * line, a description that says "filter" and nothing else.
 *
 * The quantity is here too, because refusing it does not make the shelf more
 * accurate — it makes people keep a second figure somewhere the store cannot
 * see. It asks why, and it writes a correction the stock check can read.
 */
export async function editLot(id: string, o: StoresOverview, reload: () => void): Promise<void> {
  let lot: StockLotDetail;
  try {
    lot = await api.stores.lot(id);
  } catch (err) {
    return toast(err instanceof Error ? err.message : 'That lot could not be loaded.', 'error');
  }
  amendLot(lot, o, reload);
}

function amendLot(lot: StockLotDetail, o: StoresOverview, reload: () => void): void {
  let kind = lot.kind;
  const paperwork = paperPicker({
    existing: lot.attachment ? { id: lot.attachment.id, fileName: lot.attachment.fileName } : null,
    hint: 'The Form 1, the certificate of conformity or of analysis, the delivery note — as a PDF or a photo. ' +
      'For a lot received without it, or with the wrong file.',
  });
  const onShelf = !['ISSUED', 'INSTALLED', 'DEPLETED', 'UNSALVAGEABLE', 'RETURNED_TO_SUPPLIER'].includes(lot.state);
  const dateVal = (v: string | null) => (v ? v.slice(0, 10) : '');

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Correct the details',
    sub: `${lot.partNumber} — ${lot.description.slice(0, 60)}`,
    primary: 'Save the corrections',
    body: () => {
      const acceptable = o.reference.acceptableRelease[kind] ?? [];
      const releaseChoices = o.reference.releaseKinds.filter((r) => acceptable.includes(r.kind));

      return html`
        <div class="frow3">
          ${field('What it is', select('kind', o.reference.kinds
            .map((k) => opt(k, KIND_LABEL[k] ?? k, k === kind)).join(''), 'data-kind'))}
          ${field('Part number', input('partNumber', lot.partNumber, 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('ATA chapter', input('ataChapter', lot.ataChapter ?? '', 'placeholder="79" maxlength="8"'))}
        </div>
        ${field('Description', input('description', lot.description, 'maxlength="200"'))}

        <div class="frow3">
          ${field('Batch / lot', input('batchLot', lot.batchLot ?? '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Serial number', input('serialNumber', lot.serialNumber ?? '', 'style="font-family:var(--mono)" maxlength="60"'), 'A serialised item is one item')}
          ${field('Quantity', input('quantity', String(lot.quantity), onShelf
            ? 'inputmode="decimal" style="text-align:right;font-family:var(--mono)" data-qty'
            : 'disabled style="text-align:right;font-family:var(--mono)" data-qty'),
            onShelf ? `On hand now · received as ${lot.quantityReceived} ${lot.unit}` : `${lot.stateLabel} — nothing left on the shelf`)}
        </div>
        <div class="frow3">
          ${field('Unit', select('unit', o.reference.units.map((u) => opt(u, u, u === lot.unit)).join('')))}
          ${field('Manufacturer', input('manufacturer', lot.manufacturer ?? '', 'maxlength="160"'))}
          ${field('Supplier', input('supplier', lot.supplier ?? '', 'maxlength="160"'))}
        </div>

        <div class="frow3">
          ${field('Order reference', input('orderReference', lot.orderReference ?? '', 'maxlength="60"'))}
          ${field('Manufactured / cured', input('manufacturedAt', dateVal(lot.manufacturedAt), 'type="date"'))}
          ${field('Received', input('receivedAt', dateVal(lot.receivedAt), 'type="date" disabled'), 'The receipt is history — it is not edited here')}
        </div>

        <div class="frow3">
          ${field('Shelf life expires', input('shelfLifeExpiry', dateVal(lot.shelfLifeExpiry), 'type="date"'), 'Empty where there is none')}
          ${field('Taken from', select('shelfLifeSource', [
            opt('', '—'),
            ...o.reference.shelfLifeSources.map((s) => opt(s,
              s === 'CoA' ? 'The certificate of analysis' : s === 'MANUFACTURER' ? 'The manufacturer' : 'A house rule',
              s === lot.shelfLifeSource)),
          ].join('')))}
          <div></div>
        </div>

        <div class="frow3">
          ${field('Release document', select('releaseKind', [
            opt('', 'None yet'),
            ...releaseChoices.map((r) => opt(r.kind, r.label, r.kind === lot.releaseKind)),
          ].join('')))}
          ${field('Its reference', input('releaseReference', lot.releaseReference ?? '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Issued by', input('releaseIssuer', lot.releaseIssuer ?? '', 'maxlength="160"'))}
        </div>

        <div class="fset">
          <div class="lg">The paperwork it came with</div>
          ${paperwork.body()}
        </div>

        ${field('Notes', input('notes', lot.notes ?? '', 'maxlength="1000"'))}

        ${onShelf
          ? field('Why the quantity changed', input('reason', '', 'maxlength="400" placeholder="Miscounted at receipt — three cartons, not four"'),
              'Only needed if you change the quantity. It goes on the correction, where a stock check can read it')
          : ''}

        ${callout('info', 'i', html`
          What it cost and what it sells for are on <b>Prices</b>; the condition — inspected, condemned,
          scrapped — is changed by the act itself, so that each leaves its own record.
        `)}
      `;
    },
    onClose: (submitted) => paperwork.onClose(submitted),
    onRender: (root, refresh) => {
      paperwork.bind(root, refresh);
      root.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
        kind = (e.target as HTMLSelectElement).value;
        // So the acceptable paperwork follows the class, rather than the save
        // being refused after the form is filled in.
        refresh();
      });
    },
    collect: (root) => {
      const partNumber = val(root, 'partNumber');
      const description = val(root, 'description');
      if (!partNumber) return { error: 'A part number is what everything else hangs off.' };
      if (!description) return { error: 'Describe it — a part number alone is unreadable on a shelf.' };
      const paper = paperwork.collect();
      if ('error' in paper) return paper;

      // Only what actually moved. A PATCH that sends the whole form back would
      // rewrite fields nobody touched, and blank the ones this dialog omits.
      const body: Record<string, unknown> = {};
      const put = (key: string, now: string, was: string | null) => {
        if (now !== (was ?? '')) body[key] = now;
      };
      put('partNumber', partNumber, lot.partNumber);
      put('description', description, lot.description);
      put('kind', val(root, 'kind'), lot.kind);
      put('ataChapter', val(root, 'ataChapter'), lot.ataChapter);
      put('batchLot', val(root, 'batchLot'), lot.batchLot);
      put('serialNumber', val(root, 'serialNumber'), lot.serialNumber);
      put('unit', val(root, 'unit'), lot.unit);
      put('manufacturer', val(root, 'manufacturer'), lot.manufacturer);
      put('supplier', val(root, 'supplier'), lot.supplier);
      put('orderReference', val(root, 'orderReference'), lot.orderReference);
      put('manufacturedAt', val(root, 'manufacturedAt'), dateVal(lot.manufacturedAt));
      put('shelfLifeExpiry', val(root, 'shelfLifeExpiry'), dateVal(lot.shelfLifeExpiry));
      put('shelfLifeSource', val(root, 'shelfLifeSource'), lot.shelfLifeSource);
      put('releaseKind', val(root, 'releaseKind'), lot.releaseKind);
      put('releaseReference', val(root, 'releaseReference'), lot.releaseReference);
      put('releaseIssuer', val(root, 'releaseIssuer'), lot.releaseIssuer);
      put('notes', val(root, 'notes'), lot.notes);

      const quantity = numVal(root, 'quantity');
      if (onShelf && quantity !== undefined && quantity !== lot.quantity) {
        const reason = val(root, 'reason');
        if (!reason) {
          return { error: 'Say why the quantity is changing — a stock figure that moves without a reason is the one thing a stock check cannot reconcile.' };
        }
        body.quantity = quantity;
        body.reason = reason;
      }

      if (paper.attachmentId) {
        body.attachmentId = paper.attachmentId;
        if (paper.keepPages) body.keepPages = paper.keepPages;
      }

      if (!Object.keys(body).length) return { error: 'Nothing has been changed.' };
      return body;
    },
    submit: (body) => api.stores.amend(lot.id, body),
    done: (r) =>
      r.quantity === lot.quantity
        ? `${r.partNumber} corrected`
        : `${r.partNumber} corrected · ${lot.quantity} → ${r.quantity} ${r.unit}, recorded as a correction`,
    onDone: (r: StockLotDetail) => {
      if (r.paperworkGap) toast(`${r.partNumber}: ${r.paperworkGap}`, 'error');
      reload();
    },
  });
}

/**
 * The label run.
 *
 * Lives with the other stock dialogs rather than on the store screen, because
 * it is wanted from two places now: the shelf, and the moment a delivery read
 * from its paperwork lands in quarantine needing something on the box.
 */
export function printLabels(ids: string[]): void {
  let kind: 'IDENTITY' | 'CONDITION' | 'BOTH' = 'BOTH';
  let format: 'LABEL' | 'A4' = 'LABEL';

  openDialog<{ ids: string[]; kind: string; format: string }>({
    title: `Print ${ids.length} label${ids.length === 1 ? '' : 's'}`,
    sub: 'Identity label and condition tag',
    primary: 'Produce the PDF',
    body: () => html`
      <div class="frow">
        ${field('What to print', select('kind', [
          opt('BOTH', 'Identity label and condition tag', kind === 'BOTH'),
          opt('IDENTITY', 'Identity label only', kind === 'IDENTITY'),
          opt('CONDITION', 'Condition tag only', kind === 'CONDITION'),
        ].join('')))}
        ${field('Page size', select('format', [
          opt('LABEL', '62 × 100 mm label roll', format === 'LABEL'),
          opt('A4', 'A4 sheet, 8 per page', format === 'A4'),
        ].join('')))}
      </div>
      ${callout('info', 'i', html`
        The identity label is permanent — printed once, when the lot is received. The condition tag
        changes over the lot's life, so it is reprinted whenever the condition does. Keeping them apart
        means changing a part's condition never means re-keying its batch number.
      `)}
    `,
    collect: (root) => {
      kind = val(root, 'kind') as typeof kind;
      format = val(root, 'format') as typeof format;
      return { ids, kind, format };
    },
    // Nothing is submitted — the PDF is fetched by the viewer, which needs the
    // bearer token and cannot be reached by a plain link.
    submit: async (body) => body,
    done: () => 'Labels ready',
    onDone: (body: { ids: string[]; kind: string; format: string }) => {
      openPdfViewer({
        path: '/stores/labels.pdf',
        title: `${body.ids.length} label${body.ids.length === 1 ? '' : 's'}`,
        subtitle: body.format === 'A4' ? 'A4 sheet — cut on the dashed lines' : '62 × 100 mm label roll',
        body,
        accessToken: getAccessToken,
      });
    },
  });
}

// ── the lot itself ──────────────────────────────────────────────────────────

/**
 * One lot, with everything that has happened to it and everywhere it has gone.
 *
 * The fitments list is the reason the store is worth keeping: when a
 * manufacturer recalls a batch, this is the answer rather than a week in the
 * filing room.
 */
export function showLot(id: string, o: StoresOverview, reload: () => void): void {
  let lot: StockLotDetail | null = null;

  const refetch = async (refresh: () => void) => {
    lot = await api.stores.lot(id);
    refresh();
  };

  openDialog<null>({
    wide: true,
    title: 'Stock lot',
    sub: 'Its paperwork, its history, and every aircraft it reached',
    primary: '',
    body: () => {
      if (!lot) return html`<p class="muted tiny" style="padding:20px">Loading…</p>`;
      const l = lot;

      // Read in the same shape it is corrected in: the label above the value,
      // three to a row, grouped by the question being asked. One fact per full
      // width made a lot of eight facts into a page of scrolling.
      const detail = (label: string, value: string | null, mono = false) => html`
        <div class="field">
          <label>${esc(label)}</label>
          <div class="v${mono ? ' mono' : ''}">${value ? esc(value) : '<span class="dim">—</span>'}</div>
        </div>
      `;
      const group = (legend: string, ...fields: string[]) => html`
        <div class="fset" style="margin-top:10px">
          <div class="lg">${esc(legend)}</div>
          <div class="frow3">${fields.join('')}</div>
        </div>
      `;

      return html`
        <div class="page-h" style="margin:0 0 10px">
          <div>
            <h2 style="font-family:var(--mono)">${esc(l.partNumber)}</h2>
            <p class="dim">${esc(l.description)}</p>
          </div>
          <div style="display:flex;gap:6px;align-items:center">
            ${l.tag
              ? `<span class="chip" style="${TAG_STYLE[l.tag]}">${esc(l.stateLabel)}</span>`
              : `<span class="chip">${esc(l.stateLabel)}</span>`}
            ${l.effectiveExpiry
              ? pill(l.expired ? 'OVERDUE' : l.daysToExpiry !== null && l.daysToExpiry <= 90 ? 'DUE_SOON' : 'OK', expiryText(l))
              : ''}
          </div>
        </div>

        ${l.paperworkGap ? callout('caution', '!', esc(l.paperworkGap)) : ''}
        ${l.salePrice === null && !['DEPLETED', 'INSTALLED', 'RETURNED_TO_SUPPLIER'].includes(l.state)
          ? callout('caution', '!', html`
              <b>No sale price on this lot.</b> It can still be fitted, but the release will not bill it —
              a part with no price reaches no invoice. Set one with <b>Prices</b> below.
            `)
          : ''}
        ${l.sourceAircraft
          ? callout('caution', '!', html`
              Removed from <b>${esc(l.sourceAircraft.registration)}</b>
              ${l.removedAt ? ` on ${esc(fmtDate(l.removedAt))}` : ''}
              ${l.removedBy ? ` by ${esc(l.removedBy)}` : ''}. It may only be refitted to that aircraft.
              ${l.removalNote ? html`<br>${esc(l.removalNote)}` : ''}
            `)
          : ''}

        ${group('What it is',
          detail('Class', KIND_LABEL[l.kind] ?? l.kind),
          detail('Batch / lot', l.batchLot, true),
          detail('Serial number', l.serialNumber, true),
          detail('On hand', `${l.quantity} ${l.unit}`, true),
          detail('Received as', `${l.quantityReceived} ${l.unit}`, true),
          detail('Where it is', l.location ? `${l.location.code} — ${l.location.name}` : null),
        )}

        ${group('Where it came from',
          detail('Manufacturer', l.manufacturer),
          detail('Supplier', l.supplier),
          detail('Order reference', l.orderReference, true),
          detail('Manufactured / cured', l.manufacturedAt ? fmtDate(l.manufacturedAt) : null),
          detail('Received', l.receivedAt ? fmtDate(l.receivedAt) : null),
          detail('ATA chapter', l.ataChapter, true),
        )}

        ${group('Its paperwork',
          detail('Release document', l.releaseLabel),
          detail('Its reference', l.releaseReference, true),
          detail('Issued by', l.releaseIssuer),
        )}

        ${group('The clocks, and the price',
          detail('Shelf life', l.shelfLifeExpiry
            ? `${fmtDate(l.shelfLifeExpiry)}${l.shelfLifeSource ? ` (${l.shelfLifeSource.toLowerCase().replace('_', ' ')})` : ''}`
            : 'none'),
          detail('Opened', l.openedAt
            ? `${fmtDate(l.openedAt)}${l.openedLifeDays ? ` — good for ${l.openedLifeDays} days` : ''}`
            : null),
          // Which of the two governs, said rather than left to be worked out
          // from the two dates above it.
          detail('Governed by', l.effectiveExpiry
            ? `${fmtDate(l.effectiveExpiry)} — the ${l.expiryDriver === 'OPENED' ? 'opened' : 'shelf'} clock`
            : null),
          detail('Cost', money(l.unitCost, l.currency), true),
          // Always the organisation's own money — it is what reaches an invoice.
          detail('Sale price', l.salePrice === null ? null : money(l.salePrice, o.currency), true),
          l.exchangeRate !== null
            ? detail('Rate applied', `1 ${l.currency} = ${l.exchangeRate} ${o.currency ?? ''}`.trim(), true)
            : detail('Margin', l.marginPercent === null ? null : `+${l.marginPercent}%`, true),
        )}

        ${l.unitCost !== null && l.salePrice === null && l.currency && o.currency && l.currency !== o.currency
          ? callout('caution', '!', html`
              <b>No sale price, because no rate.</b> This lot cost
              ${esc(money(l.unitCost, l.currency) ?? '')} and this organisation bills in
              <b>${esc(o.currency)}</b>. Put the rate in under <b>Prices</b> — until then the release
              will not charge for it, and nothing here will invent a figure.
            `)
          : ''}

        ${l.notes
          ? html`<div class="fset" style="margin-top:10px">
              <div class="lg">Notes</div>
              <div>${esc(l.notes)}</div>
            </div>`
          : ''}

        ${l.papers.length || l.attachment
          ? html`
              <div class="card" style="margin-top:10px">
                <div class="card-h">
                  <div>
                    <h2>Its paperwork</h2>
                    <p class="tiny dim">
                      Filed when the delivery was received — the answer to "what did this cost and who
                      sold it" a year later
                    </p>
                  </div>
                </div>
                <div class="rows">
                  ${l.attachment
                    ? html`
                        <div class="row">
                          <div class="bd">
                            <div class="t">
                              <button class="docopen" data-view="${esc(l.attachment.id)}"
                                      data-title="${esc(l.attachment.fileName)}"
                                      data-sub="${esc(l.releaseLabel ?? 'The paperwork it came with')}">
                                ${esc(l.attachment.fileName)}
                              </button>
                              <span class="sub2">${esc(l.releaseLabel ?? 'Uploaded at receipt')}${l.releaseReference ? ` · ${esc(l.releaseReference)}` : ''}</span>
                            </div>
                          </div>
                        </div>
                      `
                    : ''}
                  ${l.papers.map((p) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">
                          ${p.attachmentId
                            ? html`<button class="docopen" data-view="${esc(p.attachmentId)}"
                                     data-title="${esc(p.title)}" data-sub="${esc(PAPER_ROLE[p.role] ?? p.role)}">
                                ${esc(p.title)}
                              </button>`
                            : esc(p.title)}
                          <span class="sub2">${esc(PAPER_ROLE[p.role] ?? p.role)}</span>
                        </div>
                        ${p.note ? `<div class="m tiny dim">${esc(p.note)}</div>` : ''}
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            `
          : ''}

        ${l.inspectedAt
          ? (() => {
              // The tone follows the outcome, not the expiry. It used to read
              // `expired ? caution : ok`, which put a green tick on "shipping
              // damage — end bell cracked": a rejection presented as a clean
              // bill of health, on the one panel somebody checks before
              // deciding whether a part can be fitted.
              const failed = l.inspectionOutcome === 'FAIL';
              return callout(
                failed ? 'exceed' : l.expired ? 'caution' : 'ok',
                failed ? '✕' : '✓',
                html`
                  <b>Incoming inspection ${failed ? 'failed' : 'passed'}</b> —
                  ${esc(fmtDate(l.inspectedAt))}${l.inspectedBy ? ` by ${esc(l.inspectedBy)}` : ''}.
                  ${l.inspectionNote ? esc(l.inspectionNote) : ''}
                `,
              );
            })()
          : ''}
        ${l.mutilatedAt
          ? callout('caution', '!', html`
              Mutilated ${esc(fmtDate(l.mutilatedAt))}${l.mutilatedBy ? ` by ${esc(l.mutilatedBy)}` : ''}
              ${l.witnessedBy ? `, witnessed by ${esc(l.witnessedBy)}` : ''} —
              ${esc(l.mutilationMethod ?? '')}
            `)
          : ''}

        ${l.fitments.length
          ? html`
              <div class="card" style="margin-top:10px">
                <div class="card-h">
                  <div>
                    <h2>Where it went</h2>
                    <p class="tiny dim">Every aircraft this lot reached — the answer to a batch recall</p>
                  </div>
                </div>
                <div class="rows">
                  ${l.fitments.map((f) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t"><a href="/fleet/${esc(f.aircraftId)}" class="reg">${esc(f.aircraft)}</a>
                          <span class="sub2">${esc(f.reference)}</span></div>
                        <div class="m tiny dim">
                          ${esc(String(f.quantity))} ${esc(l.unit)} · ${esc(f.title)}
                          ${f.completedAt ? ` · completed ${esc(fmtDate(f.completedAt))}` : ' · still open'}
                        </div>
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            `
          : ''}

        <div class="card" style="margin-top:10px">
          <div class="card-h">
            <div>
              <h2>History</h2>
              <p class="tiny dim">${l.history.length} movement(s) — append-only</p>
            </div>
          </div>
          <div class="rows">
            ${l.history.map((h) => html`
              <div class="row">
                <div class="bd">
                  <div class="t">
                    ${esc(MOVEMENT_LABEL[h.kind] ?? h.kind)}
                    ${h.quantity ? `<span class="sub2">${h.quantity > 0 ? '+' : ''}${esc(String(h.quantity))} ${esc(l.unit)}</span>` : ''}
                  </div>
                  <div class="m tiny dim">
                    ${esc(fmtDate(h.at.slice(0, 10)))}${h.by ? ` · ${esc(h.by)}` : ''}
                    ${h.note ? ` · ${esc(h.note)}` : ''}
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>

        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:12px">
          ${l.state === 'QUARANTINE' ? '<button class="btn sm pri" data-act="inspect">Incoming inspection</button>' : ''}
          ${l.state === 'SERVICEABLE' && !l.openedAt ? '<button class="btn sm" data-act="open">Record as opened</button>' : ''}
          <button class="btn sm" data-act="edit">Correct the details</button>
          <button class="btn sm" data-act="price">Prices</button>
          ${l.state === 'RESERVED' ? '<button class="btn sm" data-act="unreserve">Release the reservation</button>' : ''}
          ${['QUARANTINE', 'SERVICEABLE', 'RESERVED', 'UNSERVICEABLE'].includes(l.state) ? '<button class="btn sm" data-act="move">Move it</button>' : ''}
          ${['QUARANTINE', 'SERVICEABLE', 'RESERVED'].includes(l.state) ? '<button class="btn sm" data-act="condemn">Condemn</button>' : ''}
          ${l.state === 'UNSERVICEABLE' ? '<button class="btn sm" data-act="mutilate">Scrap it</button>' : ''}
          ${['QUARANTINE', 'UNSERVICEABLE'].includes(l.state) ? '<button class="btn sm" data-act="supplier">Back to the supplier</button>' : ''}
        </div>
      `;
    },
    onRender: (root, refresh) => {
      if (!lot) {
        void refetch(refresh);
        return;
      }
      const l = lot;
      // Each action closes this dialog and opens its own: they are separate
      // acts with separate records, and stacking two modals reads badly.
      const after = () => { close(); reload(); };
      const act: Record<string, () => void> = {
        inspect: () => { close(); inspectLot(l, o, reload); },
        open: () => { close(); openLot(l, o, after); },
        unreserve: () => { close(); releaseReservation(l, reload); },
        move: () => { close(); transferLot(l, o, reload); },
        condemn: () => { close(); condemnLot(l, o, reload); },
        mutilate: () => { close(); mutilateLot(l, o, reload); },
        supplier: () => { close(); returnToSupplier(l, o, reload); },
        price: () => { close(); repriceLot(l, o, reload); },
        edit: () => { close(); amendLot(l, o, reload); },
      };
      root.querySelectorAll<HTMLElement>('[data-act]').forEach((b) =>
        b.addEventListener('click', () => act[b.dataset.act!]?.()),
      );
      // The filed papers open in the viewer, like every other document title
      // on any other screen.
      bindDocumentViewers(root);
    },
    collect: () => null,
    submit: async () => null,
    done: () => '',
  });
}
